import CampaignIcon from '@mui/icons-material/Campaign';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import ViewListIcon from '@mui/icons-material/ViewList';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import useMetaCampaignInfo from 'hooks/useMetaCampaignInfo';
import type React from 'react';

interface MetaCampaignInfoProps {
  leadId: number;
  metaAdId: number | null;
  isExpanded: boolean;
}

/**
 * Component to display Meta campaign information
 * Only shows if the lead comes from Meta and has information available
 * Only makes requests when the accordion is expanded
 */
const MetaCampaignInfoComponent: React.FC<MetaCampaignInfoProps> = ({ leadId, metaAdId, isExpanded = false }) => {
  const { metaInfo, isLoading, hasMetaInfo } = useMetaCampaignInfo(leadId, metaAdId, isExpanded);

  if (!isExpanded) {
    return null;
  }

  if (!hasMetaInfo && !isLoading) {
    return null;
  }

  if (isLoading) {
    return (
      <Paper
        elevation={2}
        sx={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          borderRadius: 3,
          p: 3,
          mb: 2,
          mt: 2,
          border: '1px solid #e3f2fd',
          textAlign: 'center',
        }}
      >
        <CircularProgress size={24} sx={{ color: '#1877f2', mb: 1 }} />
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          Cargando información de campaña Meta...
        </Typography>
      </Paper>
    );
  }

  if (!metaInfo) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: 4,
        p: 3,
        mb: 3,
        mt: 2,
        border: '2px solid #e3f2fd',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #1877f2 0%, #42a5f5 50%, #90caf9 100%)',
        },
      }}
    >
      <Divider sx={{ mb: 3, borderColor: '#e3f2fd' }} />

      {/* Campaign Information */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Campaign */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #f3f8ff 0%, #e8f4fd 100%)',
            border: '1px solid #e3f2fd',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1877f2 0%, #42a5f5 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CampaignIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: '#1877f2',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 0.5,
                display: 'block',
              }}
            >
              Campaña
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: '#1a1a1a',
                mb: 0.5,
                wordBreak: 'break-word',
              }}
            >
              {metaInfo.metaCampaignName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#666',
                fontFamily: 'monospace',
                background: '#f5f5f5',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '11px',
              }}
            >
              ID: {metaInfo.metaCampaignId}
            </Typography>
          </Box>
        </Box>

        {/* Ad Set */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #f3f8ff 0%, #e8f4fd 100%)',
            border: '1px solid #e3f2fd',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1877f2 0%, #42a5f5 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ViewListIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: '#1877f2',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 0.5,
                display: 'block',
              }}
            >
              Conjunto de Anuncios
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: '#1a1a1a',
                mb: 0.5,
                wordBreak: 'break-word',
              }}
            >
              {metaInfo.metaAdSetName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#666',
                fontFamily: 'monospace',
                background: '#f5f5f5',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '11px',
              }}
            >
              ID: {metaInfo.metaAdSetId}
            </Typography>
          </Box>
        </Box>

        {/* Ad */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #f3f8ff 0%, #e8f4fd 100%)',
            border: '1px solid #e3f2fd',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1877f2 0%, #42a5f5 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ViewInArIcon sx={{ color: 'white', fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: '#1877f2',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 0.5,
                display: 'block',
              }}
            >
              Anuncio
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: '#1a1a1a',
                mb: 0.5,
                wordBreak: 'break-word',
              }}
            >
              {metaInfo.metaAdName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#666',
                fontFamily: 'monospace',
                background: '#f5f5f5',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '11px',
              }}
            >
              ID: {metaInfo.metaAdId}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default MetaCampaignInfoComponent;
